import { useGroupChannelListContext } from '@sendbird/uikit-react/GroupChannelList/context';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import { VariableSizeList as List } from 'react-window';
import styled, { css } from 'styled-components';

import { ICON_ALIASES } from '_constants/icons';
import { theme } from '_constants/theme';
import { hexToRGBA } from '_helpers/theme';
import { refreshData } from '_store/chat/slice';
import { selectCompanyNamesMap } from '_store/orders/selector';

import Icon from 'components/atomic/Icon';
import Text from 'components/atomic/layouts/Text';

import { parseChannelData } from '../../helpers';

const StyledListContainer = styled.div`
  scrollbar-color: ${theme.colors.gray.secondary} ${theme.colors.linkWater};
`;

const StyledListItemOuterContainer = styled.div``;

const StyledListItemInnerContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: ${theme.spaces.smallX}px;
  position: relative;
  padding-top: ${theme.spaces.regular}px;
  padding-bottom: ${theme.spaces.regular}px;
  padding-right: ${theme.spaces.regular}px;
  padding-left: ${theme.spaces.smallX}px;
  border: 1px solid ${hexToRGBA(theme.colors.gray.secondary, 0.16)};
  border-radius: 16px;
  cursor: pointer;

  &:hover {
    background-color: ${hexToRGBA(theme.colors.gray.secondary, 0.08)};
  }

  ${({ selected }) =>
    selected &&
    css`
      border: 1px solid ${theme.colors.indigo.primary};
      background-color: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};

      &:hover {
        background-color: ${hexToRGBA(theme.colors.indigo.primary, 0.08)};
      }
    `}
`;

const StyledGroupChannelListItemIconContainer = styled.div`
  padding: ${theme.spaces.smallX}px;
`;

const StyledGroupChannelListItemContent = styled.div`
  flex-grow: 1;
`;

const StyledBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 8px;
  padding-right: 8px;
  border-radius: 12px;
  background-color: #dc3545;
  font-size: 14px;
  line-height: 14px;
  font-weight: 700;
  color: #ffffff;
`;

const useResize = () => {
  const [size, setSize] = useState([0, 0]);

  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }

    window.addEventListener('resize', updateSize);
    updateSize();

    return () => window.removeEventListener('resize', updateSize);
  }, []);

  return size;
};

// eslint-disable-next-line react/prop-types
const ListItem = ({ index, style, data }) => {
  const listItemRef = useRef();
  const { groupChannels, selectedChannelUrl, companyNamesMap, handleSelect, windowSize, setItemSize } = data || {};
  const channel = groupChannels[index];
  const { productId } = parseChannelData(channel?.data);

  useEffect(() => {
    setItemSize && setItemSize({ index, size: listItemRef.current.getBoundingClientRect().height });
  }, [setItemSize, index, windowSize, data]);

  return (
    <StyledListItemOuterContainer style={{ ...style, paddingBottom: 16, paddingRight: 8 }}>
      <StyledListItemInnerContainer
        ref={listItemRef}
        selected={channel?.url === selectedChannelUrl}
        onClick={handleSelect(channel)}
      >
        {Boolean(channel.unreadMessageCount) && (
          <StyledBadge>{channel.unreadMessageCount > 98 ? '99+' : channel.unreadMessageCount}</StyledBadge>
        )}
        <StyledGroupChannelListItemIconContainer>
          <Icon alias={ICON_ALIASES.message} color={theme.colors.indigo.primary} />
        </StyledGroupChannelListItemIconContainer>
        <StyledGroupChannelListItemContent>
          <Text desktop size={theme.fontSizes.regular} weight={600} color={theme.colors.gray.primary}>
            {companyNamesMap[productId] || 'N/A'}
          </Text>
          <Text mobile size={theme.fontSizes.small} weight={600} color={theme.colors.gray.primary}>
            {companyNamesMap[productId] || 'N/A'}
          </Text>
          <Text desktop size={theme.fontSizes.small} weight={400} color={theme.colors.gray.secondary}>
            {channel.name}
          </Text>
          <Text mobile size={theme.fontSizes.smallX} weight={400} color={theme.colors.gray.secondary}>
            {channel.name}
          </Text>
        </StyledGroupChannelListItemContent>
      </StyledListItemInnerContainer>
    </StyledListItemOuterContainer>
  );
};

const CustomGroupChannelListView = () => {
  const listRef = useRef();
  const listItemSizeMap = useRef({});
  const { groupChannels, selectedChannelUrl } = useGroupChannelListContext();
  const dispatch = useDispatch();
  const companyNamesMap = useSelector(selectCompanyNamesMap);

  const handleSelect = (channel) => () => {
    dispatch(refreshData({ channelUrl: channel.url }));
  };

  const setItemSize = ({ index, size }) => {
    listItemSizeMap.current = { ...listItemSizeMap.current, [index]: size + 16 };
    listRef.current.resetAfterIndex(index);
  };

  const getItemSize = (index) => listItemSizeMap.current[index] || 140;

  const [windowSize] = useResize();

  return (
    <StyledListContainer>
      <List
        ref={listRef}
        height={isMobile ? 260 : 480}
        width="100%"
        itemCount={groupChannels?.length}
        itemSize={getItemSize}
        itemData={{
          groupChannels,
          selectedChannelUrl,
          companyNamesMap,
          handleSelect,
          windowSize,
          setItemSize,
        }}
      >
        {ListItem}
      </List>
    </StyledListContainer>
  );
};

export default CustomGroupChannelListView;
